import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Loader2, Moon, Sun } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth'
import { useTheme } from '../hooks/useTheme'
import { ApiError, getMe, oauthStartURL } from '../lib/api'
import LunoWordmark from '../components/LunoWordmark'
import type { User } from '../types'
import {
  brandButton,
  glassCard,
  pageBackground,
  textBody,
  textMuted,
} from '../styles/tokens'

// text-base on mobile keeps the input at 16px so iOS Safari doesn't
// auto-zoom on focus; we drop to text-sm at sm: and up to match the
// rest of the auth UI density.
const inputClass =
  'w-full rounded-xl border border-black/[0.06] bg-surface-light px-4 py-2.5 text-base text-v2-ink placeholder:text-v2-ink-muted transition focus:border-v2-purple/60 focus:outline-none focus:ring-2 focus:ring-v2-purple/30 sm:text-sm dark:border-bg-line dark:bg-bg-raised dark:text-v2-ink-inv dark:placeholder:text-v2-ink-inv-muted dark:focus:border-v2-purple-dark/60 dark:focus:ring-v2-purple-dark/30'

export default function LoginPage() {
  // Load both namespaces — `auth` for the page chrome, `common` for the
  // shared footer links and theme-toggle aria-label. useTranslation
  // accepts an array of namespaces so the `t` function resolves keys
  // from either bundle.
  const { t } = useTranslation(['auth', 'common'])
  const { login, register, setSession } = useAuth()
  const { theme, toggle } = useTheme()
  const [searchParams] = useSearchParams()
  // Build-time flag: the button renders only when the backend is wired for
  // Google sign-in, mirroring the server's all-or-nothing GOOGLE_OAUTH_* gate.
  const googleEnabled =
    (import.meta.env.VITE_GOOGLE_OAUTH_ENABLED as string | undefined) === 'true'
  // After the OAuth callback the browser lands here with ?signed_in=google and
  // a fresh session cookie but no cached user blob — so useAuth's mount-time
  // /me refresh (gated on a stored blob) never fires. Start in the hydrating
  // state so we render a spinner, not a flash of the form, while we confirm.
  const [hydrating, setHydrating] = useState(
    () => searchParams.get('signed_in') === 'google',
  )
  const [isRegister, setIsRegister] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  // UK ICO Children's Code age-gate. Required when submitting the register
  // form; the input also carries `required` so a JS-disabled / programmatic
  // submission still hits the browser's native constraint validation.
  const [confirmedMinimumAge, setConfirmedMinimumAge] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  // Tick down to zero once the server rate-limits us, to both re-enable the
  // submit button and give the user a concrete ETA instead of a stale toast.
  const [retryIn, setRetryIn] = useState(0)

  useEffect(() => {
    if (retryIn <= 0) return
    const id = window.setInterval(() => {
      setRetryIn(s => (s <= 1 ? 0 : s - 1))
    }, 1000)
    return () => window.clearInterval(id)
  }, [retryIn])

  // Handle the OAuth callback landing. On success, GET /me confirms the cookie
  // and setSession flips us authed — App's /login route then redirects to "/".
  // On failure the backend sent ?error=google; surface a generic message.
  // Runs once on mount; the query params don't change without a navigation.
  useEffect(() => {
    if (searchParams.get('error') === 'google') {
      setError(t('auth:login.error_oauth'))
      return
    }
    if (searchParams.get('signed_in') === 'google') {
      getMe()
        .then(u => setSession('', u as User))
        .catch(() => {
          setHydrating(false)
          setError(t('auth:login.error_oauth'))
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (retryIn > 0) return
    setError('')
    setLoading(true)

    try {
      if (isRegister) {
        await register(email, password, name, confirmedMinimumAge)
      } else {
        await login(email, password)
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        const wait = err.retryAfterSeconds && err.retryAfterSeconds > 0 ? err.retryAfterSeconds : 60
        setRetryIn(wait)
        setError(t('auth:login.error_rate_limit', { seconds: wait }))
      } else {
        setError(err instanceof Error ? err.message : t('auth:login.error_generic'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={`${pageBackground} relative flex min-h-screen flex-col items-center justify-center gap-6 px-4 py-8`}>
      <button
        type="button"
        onClick={toggle}
        aria-label={t('auth:login.theme_toggle')}
        className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-black/[0.06] bg-surface-white text-v2-ink-muted shadow-sm transition hover:scale-[1.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-purple/40 dark:border-bg-line dark:bg-bg-raised dark:text-v2-ink-inv-muted"
      >
        {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
      </button>

      <div className={`${glassCard} w-full max-w-sm rounded-[24px] p-8`}>
        {/* "Luno Learning" is a brand name and intentionally not
            translated — same convention English-loaned product names
            follow on other SaaS in every locale we ship. The wordmark
            carries the brand visually; an sr-only h1 keeps the heading
            in the a11y tree without duplicating it on screen. */}
        <h1 className="sr-only">Luno Learning</h1>
        <div className="mb-4 flex justify-center">
          <LunoWordmark height={40} />
        </div>
        <p className={`mt-2 text-center text-sm ${textBody}`}>
          {isRegister
            ? t('auth:login.subtitle_register')
            : t('auth:login.subtitle_signin')}
        </p>

        {hydrating ? (
          <div className="mt-8 flex flex-col items-center gap-3 py-6">
            <Loader2 size={24} className="animate-spin text-v2-purple dark:text-v2-purple-dark" />
            <p className={`text-sm ${textBody}`}>{t('auth:login.oauth_signing_in')}</p>
          </div>
        ) : (
          <>
        <form onSubmit={handleSubmit} className="mt-8 space-y-4">
          {isRegister && (
            <div>
              <label htmlFor="login-name" className="sr-only">
                {t('auth:login.name_label')}
              </label>
              <input
                id="login-name"
                type="text"
                placeholder={t('auth:login.name_placeholder')}
                value={name}
                onChange={e => setName(e.target.value)}
                required
                autoComplete="name"
                className={inputClass}
              />
            </div>
          )}
          <div>
            <label htmlFor="login-email" className="sr-only">
              {t('auth:login.email_label')}
            </label>
            <input
              id="login-email"
              type="email"
              placeholder={t('auth:login.email_placeholder')}
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoComplete="email"
              inputMode="email"
              autoCapitalize="off"
              spellCheck={false}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="login-password" className="sr-only">
              {t('auth:login.password_label')}
            </label>
            <input
              id="login-password"
              type="password"
              placeholder={t('auth:login.password_placeholder')}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              minLength={isRegister ? 12 : 1}
              autoComplete={isRegister ? 'new-password' : 'current-password'}
              autoCapitalize="off"
              spellCheck={false}
              className={inputClass}
            />
          </div>
          {isRegister && (
            <p className={`text-xs ${textMuted}`}>
              {t('auth:login.password_hint')}
            </p>
          )}

          {isRegister && (
            <label
              htmlFor="login-age-confirm"
              className={`flex items-start gap-2 text-xs ${textBody}`}
            >
              <input
                id="login-age-confirm"
                type="checkbox"
                required
                checked={confirmedMinimumAge}
                onChange={e => setConfirmedMinimumAge(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-black/20 bg-surface-white text-v2-purple focus:ring-2 focus:ring-v2-purple/30 dark:border-bg-line dark:bg-bg-raised dark:focus:ring-v2-purple-dark/30"
              />
              <span>{t('auth:login.age_confirm')}</span>
            </label>
          )}

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {retryIn > 0
                ? t('auth:login.error_rate_limit', { seconds: retryIn })
                : error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading || retryIn > 0}
            className={`${brandButton} w-full py-2.5 text-sm disabled:opacity-50 disabled:hover:scale-100`}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 size={14} className="animate-spin" />
                {isRegister
                  ? t('auth:login.button_creating')
                  : t('auth:login.button_signing_in')}
              </span>
            ) : retryIn > 0 ? (
              t('auth:login.button_retry_in', { seconds: retryIn })
            ) : isRegister
              ? t('auth:login.button_create')
              : t('auth:login.button_signin')}
          </button>

          {isRegister && (
            <p className={`text-center text-xs ${textMuted}`}>
              {/* Inline links inside a translated sentence are the canonical
                  Trans-component case — translators see the full sentence
                  with placeholder tags instead of three disconnected
                  fragments. Link styling stays in code; <termsLink> and
                  <privacyLink> in the key map to the components prop. */}
              <Trans
                i18nKey="auth:login.terms_agreement"
                components={{
                  termsLink: (
                    <Link
                      to="/terms"
                      className="underline underline-offset-2 transition hover:text-v2-purple dark:hover:text-v2-purple-dark"
                    />
                  ),
                  privacyLink: (
                    <Link
                      to="/privacy"
                      className="underline underline-offset-2 transition hover:text-v2-purple dark:hover:text-v2-purple-dark"
                    />
                  ),
                }}
              />
            </p>
          )}

          {!isRegister && (
            <p className={`text-center text-xs ${textMuted}`}>
              <Link
                to="/forgot-password"
                className="transition hover:text-v2-purple dark:hover:text-v2-purple-dark"
              >
                {t('auth:login.forgot_password')}
              </Link>
            </p>
          )}
        </form>

        {googleEnabled && (
          <>
            <div className="my-6 flex items-center gap-3" aria-hidden="true">
              <span className="h-px flex-1 bg-v2-ink/10 dark:bg-white/10" />
              <span className={`text-xs ${textMuted}`}>
                {t('auth:login.oauth_divider')}
              </span>
              <span className="h-px flex-1 bg-v2-ink/10 dark:bg-white/10" />
            </div>
            <a
              href={oauthStartURL('google')}
              className="flex w-full items-center justify-center gap-2.5 rounded-xl border border-black/[0.06] bg-surface-light px-4 py-2.5 text-sm font-medium text-v2-ink transition hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-purple/30 dark:border-bg-line dark:bg-bg-raised dark:text-v2-ink-inv"
            >
              <GoogleGlyph />
              {t('auth:login.oauth_google')}
            </a>
          </>
        )}

        <p className={`mt-6 text-center text-sm ${textMuted}`}>
          {isRegister
            ? t('auth:login.toggle_prompt_to_signin')
            : t('auth:login.toggle_prompt_to_register')}{' '}
          <button
            onClick={() => {
              setIsRegister(!isRegister)
              setError('')
            }}
            className="font-semibold text-v2-purple transition hover:underline dark:text-v2-purple-dark"
          >
            {isRegister
              ? t('auth:login.toggle_button_signin')
              : t('auth:login.toggle_button_register')}
          </button>
        </p>
          </>
        )}
      </div>

      <div className={`flex items-center gap-3 text-xs ${textMuted}`}>
        <Link
          to="/privacy"
          className="transition hover:text-v2-purple dark:hover:text-v2-purple-dark"
        >
          {t('common:legal.privacy_policy_link')}
        </Link>
        <span aria-hidden="true">·</span>
        <Link
          to="/terms"
          className="transition hover:text-v2-purple dark:hover:text-v2-purple-dark"
        >
          {t('common:legal.terms_link')}
        </Link>
      </div>
    </div>
  )
}

// Official Google "G" mark. Google's branding guidelines require the
// multicolour glyph on "Sign in with Google" affordances, and lucide ships no
// brand logos — so it's inlined rather than pulled from an icon set.
function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  )
}
